import { Event } from '../types';

/**
 * Alerts definition — matches the APP dictionary (config.ts ALERTS_DEFINITION).
 * Used when `registered_when` is set (APP synthetic events / remote orders).
 */
const ALERTS_DEFINITION: { [key: string]: string } = {
  OPEN: 'Open',
  CLOSE: 'Close',
  SET_TEMPORIZED: 'Open with temporization',
  SYNC_NFC_REQUIRED: 'Sync new NFC card required',
  SYNC_PHONE_BEACON_REQUIRED: 'Sync new Phone/Beacon required',
  NFC_OPEN: 'Open with NFC',
  NFC_CLOSE: 'Close with NFC',
};

/**
 * Event type definitions — aligned with the APP dictionary (config.ts EVENTS_DEFINITIONS).
 * NFC user events (type >= 128) are handled dynamically instead of 100 separate entries.
 */
const eventTypes: { [key: number]: string } = {
  0: 'Undefined order',
  1: 'App: No action',
  2: 'App: Opening',
  3: 'App: Closing',
  4: 'Master: No action',
  5: 'Master: Opening',
  6: 'Master: Closing',
  7: 'Superuser: No action',
  8: 'Superuser: Opening',
  9: 'Superuser: Closing',
  10: 'Event download',
  11: 'Temporary opening order',
  12: 'Access Mode Changed',
  13: 'Threshold Changed',
  14: 'Bin Type Changed',
  15: 'Buzzer Alarm Changed',
  16: 'Sync new NFC card required',
  17: 'Sync new Phone/Beacon required',
  100: 'Soft Lift Detected',
  101: 'Lift Detected',
  102: 'Inverse Open Event',
  103: 'Beacon Open Event',
  104: 'Beacon Close Event',
  105: 'Battery Low Event',
  122: 'Cold Reset Event',
  123: 'Watchdog Reset Event',
  124: 'Other Reset Event',
  125: 'Not Authorized Connection Event',
  126: 'APP synthetic event',
  127: 'NFC Not Auth',
};

export const getEventDescription = (event: Event): string => {
  const { type, registered_when } = event;

  // APP synthetic events: use ALERTS_DEFINITION like the APP does
  if (registered_when) {
    const alertKey = registered_when.toUpperCase();
    return ALERTS_DEFINITION[alertKey] || `${registered_when.charAt(0).toUpperCase() + registered_when.slice(1).toLowerCase()}`;
  }

  // NFC auth user events (type 128+): dynamic label with user index
  if (type >= 128) {
    const userIndex = type - 127;
    return `NFC Auth User ${userIndex}`;
  }

  return eventTypes[type] || `Unknown event (${type})`;
};

/**
 * Returns the user display string for the event.
 * - For NFC events (type >= 128): the stored NFC card name, falling back to "NFC User {n}"
 * - For APP synthetic and other events: the user_name if available
 */
export const getEventUserDisplay = (event: Event): string => {
  const userName = event.user_name?.trim();
  if (userName) {
    return userName;
  }

  if (event.type >= 128) {
    return `NFC User ${event.type - 127}`;
  }
  return '';
};
